import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import type { ProviderId } from "../../../types/lifecycle.ts";
import type { LifecycleController } from "./useLifecycle.ts";

/** The vehicle, owner and insurer the calculator hands over. */
export interface CalculatorHandoff {
  provider: ProviderId;
  plate: string;
  certNumber: string;
  pin: string;
}

// The former insurer pages the calculator still links to.
const INSURER_PAGES: Record<string, ProviderId> = {
  ozk: "ozk",
  dzi: "dzi",
  generali: "generali",
  levins: "levins",
};
const STATE_KEY = "calculatorHandoff";

const text = (value: unknown): string =>
  typeof value === "string" || typeof value === "number"
    ? String(value).trim()
    : "";

/** The calculator's router state (CalcNavigationState) for an insurer page. */
export function readCalculatorState(
  insurer: string,
  state: unknown
): CalculatorHandoff | null {
  const provider = INSURER_PAGES[insurer];
  const value = (state && typeof state === "object" ? state : {}) as {
    vehicle?: Record<string, unknown>;
    owner?: Record<string, unknown>;
  };
  const plate = text(value.vehicle?.regNo);
  const certNumber = text(value.vehicle?.certNumber);
  if (!provider || !plate || !certNumber) return null;
  return { provider, plate, certNumber, pin: text(value.owner?.pid) };
}

/**
 * The calculator still opens /integrations/{insurer} with what it priced. Those
 * pages are gone, so this hands the data to the unified issuance page.
 */
export function CalculatorRedirect({ insurer }: { insurer: string }) {
  const { state } = useLocation();
  const handoff = readCalculatorState(insurer, state);
  return (
    <Navigate
      to="/policies/issue"
      replace
      state={handoff ? { [STATE_KEY]: handoff } : null}
    />
  );
}

/** The handoff the issuance page was opened with, read once and then cleared. */
export function useCalculatorHandoff(): CalculatorHandoff | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [handoff] = useState<CalculatorHandoff | null>(() => {
    const value = (location.state as Record<string, unknown> | null)?.[STATE_KEY];
    return value && typeof value === "object" ? (value as CalculatorHandoff) : null;
  });
  // Consumed, so a reload or a return to the page does not quote it again.
  useEffect(() => {
    if (handoff)
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
  }, []);
  return handoff;
}

/** Quote the handed-over vehicle once, then select the insurer chosen there. */
export function useCalculatorQuote(
  controller: LifecycleController,
  handoff: CalculatorHandoff | null
) {
  const searched = useRef(false);
  const picked = useRef(false);
  useEffect(() => {
    if (!handoff || searched.current) return;
    searched.current = true;
    void controller.search();
  }, [handoff]);
  const offers = controller.quote?.offers;
  useEffect(() => {
    if (!handoff || picked.current || !offers) return;
    picked.current = true;
    if (
      offers.some(
        (offer) =>
          offer.provider === handoff.provider && offer.available && offer.canIssue
      )
    )
      controller.quoteForm.setProvider(handoff.provider);
  }, [offers, handoff]);
}
